/* eslint-disable react/prop-types */
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useState } from 'react';
// api
import { getCities } from '../../../employersSide/api/getCities';
// components
import Input from '../../components/Input';
import InputDate from '../../components/InputDate';
import InputRadio from '../../components/InputRadio';
import SelectReact from '../../components/SelectReact';
import AsyncSelectReact from '../../components/AsyncSelectReact';
// helpers
import { debounceAsync } from '../../../helpers';
// hooks
import { useWindowSize } from '../../../../Hooks/use-window-size';

const getCountryName = (id, array) => {
  const country = array.find(item => item.id == id);

  return country ? { label: country.name, value: country.id } : undefined;
};

const PersonalInformationBlock = ({
  errors,
  values,
  touched,
  // setErrors,
  directories,
  handleChange,
  validateForm,
  setFieldValue,
  // setFieldError,
  setFieldTouched,
}) => {
  const { width } = useWindowSize();
  const { t } = useTranslation('global');

  const { countries = [] } = directories;

  const {
    city = '',
    gender = '',
    country = '',
    birthday = '',
    last_name = '',
    first_name = '',
    middle_name = '',
  } = values;

  // const [prevCity, setPrevCity] = useState('');
  const [lastName, setLastName] = useState(last_name || '');
  const [firstName, setFirstName] = useState(first_name || '');
  const [middleName, setMiddleName] = useState(middle_name || '');
  const [birthdayLocal, setBirthdayLocal] = useState(birthday || '');

  const genderFields = [
    {
      value: '0',
      name: 'female',
      checkedValue: gender,
      handleChange: handleChange,
      label: t('jobseekerEditProfile.female'),
      handleBlur: () => setFieldTouched('gender', true),
    },
    {
      value: '1',
      name: 'male',
      checkedValue: gender,
      handleChange: handleChange,
      label: t('jobseekerEditProfile.male'),
      handleBlur: () => setFieldTouched('gender', true),
    },
  ];

  const getCitiesOptions = async inputValue => {
    if (!country || inputValue.length < 2) {
      return [];
    }

    try {
      const cities = await getCities(parseInt(country), inputValue);

      if (cities.data.response?.length === 0) {
        throw new Error('No options found');
      }

      const options = cities.data.response.map(city => ({
        value: city.id,
        label: `${city.name}${city.region && `, ${city.region}`}`,
      }));

      return options;
    } catch (error) {
      console.error('Error fetching cities:', error);
      return [];
    }
  };

  // const handleFocus = async currentCity => {
  //   setPrevCity(currentCity);
  //   await setFieldValue('city', '');
  //   await setFieldError('city', '');
  // };

  // const handleBlur = async currentCity => {
  //   if (!currentCity) {
  //     await setFieldValue('city', prevCity);

  //     if (prevCity !== '') {
  //       const currentErrors = { ...errors };

  //       delete currentErrors.city;

  //       await setErrors(currentErrors);
  //     }
  //   }
  // };

  const handleInputChange = useCallback(e => {
    const { name, value } = e.target;

    if (name === 'last_name') {
      setLastName(value);
    } else if (name === 'first_name') {
      setFirstName(value);
    } else if (name === 'middle_name') {
      setMiddleName(value);
    } else {
      setBirthdayLocal(value);
    }

    debouncedHandleInputChange(name, value);
  }, []); // eslint-disable-line

  const handleSelectChange = (name, selectedOption) => {
    if (selectedOption) {
      if (name === 'country') {
        setFieldValue(name, selectedOption.value);
      } else {
        setFieldValue(name, selectedOption);
      }
    } else {
      setFieldValue(name, '');
    }
  };

  const debouncedGetCitiesOptions = debounceAsync(getCitiesOptions, 500);

  const debouncedHandleInputChange = debounce((name, value) => {
    setFieldValue(name, value);
  }, 500);

  useEffect(() => {
    validateForm();
    // if (width >= 768) {
    //   window.scrollTo(0, 0);
    // } else {
    //   window.scrollTo({ top: 280, behavior: 'smooth' });
    // }
  }, [width, validateForm]);

  return (
    <>
      <Input
        required
        name='first_name'
        value={firstName}
        error={errors.first_name}
        touched={touched.first_name}
        errorMessage={errors.first_name}
        handleChange={e => handleInputChange(e)}
        label={t('jobseekerEditProfile.firstName')}
        placeholder={t('jobseekerEditProfile.firstName')}
        handleBlur={() => setFieldTouched('first_name', true)}
      />
      <Input
        required
        name='last_name'
        value={lastName}
        error={errors.last_name}
        touched={touched.last_name}
        errorMessage={errors.last_name}
        handleChange={e => handleInputChange(e)}
        label={t('jobseekerEditProfile.lastName')}
        placeholder={t('jobseekerEditProfile.lastName')}
        handleBlur={() => setFieldTouched('last_name', true)}
      />
      <Input
        name='middle_name'
        value={middleName}
        error={errors.middle_name}
        touched={touched.middle_name}
        errorMessage={errors.middle_name}
        handleChange={e => handleInputChange(e)}
        label={t('jobseekerEditProfile.middleName')}
        placeholder={t('jobseekerEditProfile.middleName')}
        handleBlur={() => setFieldTouched('middle_name', true)}
      />
      <InputRadio
        required
        groupName='gender'
        error={errors.gender}
        fields={genderFields}
        touched={touched.gender}
        groupLabel={t('jobseekerEditProfile.gender')}
      />
      <InputDate
        required
        name='birthday'
        minDate='1930-01-01'
        value={birthdayLocal}
        error={errors.birthday}
        touched={touched.birthday}
        handleChange={e => handleInputChange(e)}
        label={t('jobseekerEditProfile.birthday')}
        maxDate={new Date().toISOString().split('T')[0]}
        handleBlur={() => setFieldTouched('birthday', true)}
      />
      <SelectReact
        required
        isClearable
        isSearchable
        name='country'
        error={errors.country}
        touched={touched.country}
        label={t('jobseekerEditProfile.country')}
        value={getCountryName(country, countries)}
        handleBlur={() => setFieldTouched('country', true)}
        placeholder={t('jobseekerEditProfile.selectCountry')}
        options={countries.map(i => ({ value: i.id, label: i.name }))}
        noOptionsMessage={() => t('jobseekerEditProfile.noOptionsFound')}
        handleChange={selectedOptions => {
          handleSelectChange('country', selectedOptions);
          setFieldValue('city', '');
        }}
      />
      {
        country && (
          <AsyncSelectReact
            required
            isClearable
            name='city'
            value={city}
            className='mb-5'
            error={errors.city}
            touched={touched.city}
            label={t('jobseekerEditProfile.city')}
            loadOptions={debouncedGetCitiesOptions}
            placeholder={t('jobseekerEditProfile.enterCityName')}
            loadingMessage={() => t('jobseekerEditProfile.pleaseWait')}
            handleChange={selectedOptions => handleSelectChange('city', selectedOptions)}
            noOptionsMessage={
              ({ inputValue }) => inputValue.length >= 2
                ? t('jobseekerEditProfile.noOptionsFound')
                : t('jobseekerEditProfile.enterCityName')
            }
            // handleFocus={() => {
            //   if (width >= 1200) {
            //     handleFocus(city);
            //   }
            // }}
            handleBlur={() => {
              // if (width >= 1200) {
              //   handleBlur(city);
              // }

              setTimeout(() => {
                setFieldTouched('city', true);
              }, 200);
            }}
          />
        )
      }
    </>
  );
};

export default PersonalInformationBlock;
